import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { AuthGate } from '@/shared/auth/AuthGate'
import { RequireAuth, RequireAnonymous } from '@/shared/auth/guards'
import { AppShell } from '@/components/tessera/AppShell'
import { ElectionProvider } from '@/shared/election/context'
import { LoginPage } from '@/features/auth/LoginPage'
import { ForcedPasswordChangePage } from '@/features/auth/ForcedPasswordChangePage'
import { AcceptInvitationPage } from '@/features/auth/AcceptInvitationPage'
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage'
import { AdministrationModule } from '@/features/administration'
import { VictoryPage } from '@/features/victory/VictoryPage'
import { HistoryPage } from '@/features/historical/HistoryPage'
import { AuditLogPage } from '@/features/audit/AuditLogPage'
import { NotificationsPage } from '@/features/notifications/NotificationsPage'
import { UsersPage } from '@/features/users/UsersPage'
import { RolesPage } from '@/features/roles/RolesPage'
import { GeographyPage } from '@/features/geography/GeographyPage'
import { VotersPage } from '@/features/voters/VotersPage'
import { TagManagerPage } from '@/features/voters/TagManagerPage'
import { IntegrationsPage } from '@/features/integrations/IntegrationsPage'
import { IntegrationDetailPage } from '@/features/integrations/IntegrationDetailPage'
import { ProviderPage } from '@/features/integrations/ProviderPage'
import { TransfersPage } from '@/features/relocation/TransfersPage'
import { TransferDetailPage } from '@/features/relocation/TransferDetailPage'
import { TransferAnalysisPage } from '@/features/relocation/AnalysisPage'
import { MobilizerRosterPage } from '@/features/mobilizers/RosterPage'
import { MobilizerHierarchyPage } from '@/features/mobilizers/HierarchyPage'
import { MobilizerCoveragePage } from '@/features/mobilizers/CoveragePage'
import { MobilizerAdoptionPage } from '@/features/mobilizers/AdoptionPage'
import { MobilizerActivityPage } from '@/features/mobilizers/ActivityFeedPage'
import { MobilizerMissionsPage } from '@/features/mobilizers/MissionsPage'
import { MobilizerLeaderboardPage } from '@/features/mobilizers/LeaderboardPage'
import { MobilizerAnalyticsPage } from '@/features/mobilizers/AnalyticsPage'
import { MyDayPage } from '@/features/mobilizers/MyDayPage'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { ProgramsListPage } from '@/features/programs/ProgramsListPage'
import { ProgramAnalyticsLayout } from '@/features/programs/analytics/AnalyticsLayout'
import { ProgramOverviewScreen } from '@/features/programs/analytics/OverviewScreen'
import { ProgramCouncilScreen } from '@/features/programs/analytics/CouncilScreen'
import { ProgramTargetGroupsScreen } from '@/features/programs/analytics/TargetGroupsScreen'
import { ProgramOverlapScreen } from '@/features/programs/analytics/OverlapScreen'
import { ProgramCostVsUpliftScreen } from '@/features/programs/analytics/CostVsUpliftScreen'
import { ProgramEcosystemScreen } from '@/features/programs/analytics/EcosystemScreen'
import { ProgramCoverageScreen } from '@/features/programs/analytics/CoverageScreen'
import { ProgramGeographyScreen } from '@/features/programs/analytics/GeographyScreen'
import { ProgramMoneyScreen } from '@/features/programs/analytics/MoneyScreen'
import { ProgramImpactScreen } from '@/features/programs/analytics/ImpactScreen'
import { ProgramEquityScreen } from '@/features/programs/analytics/EquityScreen'
import { ProgramQualityScreen } from '@/features/programs/analytics/QualityScreen'
import { ProgramAlertsScreen } from '@/features/programs/analytics/AlertsScreen'
import { LiveCoveragePage } from '@/features/coverage/LiveNowPage'
import { CoverageEventsPage } from '@/features/coverage/EventsPage'
import { CoverageArchivePage } from '@/features/coverage/ArchivePage'
import { CoverageGoLivePage } from '@/features/coverage/GoLivePage'
import { CoverageAssignmentsPage } from '@/features/coverage/AssignmentsPage'
import { CoverageAnalyticsPage } from '@/features/coverage/AnalyticsPage'
import { CoverageMapPage } from '@/features/coverage/MapPage'
import { BroadcastStandalonePage } from '@/features/coverage/BroadcastStandalonePage'
import { EstimatorPage } from '@/features/polling/EstimatorPage'
import { PollingLayout } from '@/features/polling/PollingLayout'
import { PollingOverviewPage } from '@/features/polling/OverviewPage'
import { PollsListPage } from '@/features/polling/PollsPage'
import { FieldworkPage } from '@/features/polling/FieldworkPage'
import { QualityPage } from '@/features/polling/QualityPage'
import { InterviewsPage } from '@/features/polling/InterviewsPage'
import { BuilderPage } from '@/features/polling/BuilderPage'
import { ConfiguratorPage } from '@/features/polling/ConfiguratorPage'
import { SamplePage } from '@/features/polling/SamplePage'
import { ResultsPage } from '@/features/polling/ResultsPage'
import { CrosstabsPage } from '@/features/polling/CrosstabsPage'
import { SegmentsPage } from '@/features/polling/SegmentsPage'
import { VerbatimsPage } from '@/features/polling/VerbatimsPage'
import { ExportsPage } from '@/features/polling/ExportsPage'
import { ExternalPollsPage } from '@/features/polling/ExternalPollsPage'
import { MyAssignmentsPage } from '@/features/polling/MyAssignmentsPage'
import { CapturePage } from '@/features/polling/CapturePage'
import { TrackersPage } from '@/features/polling/TrackersPage'
import { MapPage } from '@/features/polling/MapPage'
import { PoliticsLayout } from '@/features/politics/PoliticsLayout'
import { OverviewPage as PoliticsOverviewPage } from '@/features/politics/OverviewPage'
import { PartiesPage } from '@/features/politics/PartiesPage'
import { CoalitionsPage } from '@/features/politics/CoalitionsPage'
import { CandidatesPage } from '@/features/politics/CandidatesPage'

import { StationMatesLayout } from '@/features/station-mates/StationMatesLayout'
import { StationMatesOverviewPage } from '@/features/station-mates/OverviewPage'
import { FloorPage } from '@/features/station-mates/FloorPage'
import { ElectionsPage } from '@/features/elections/ElectionsPage'
import { StationsPage } from '@/features/station-mates/StationsPage'
import { CellsPage } from '@/features/station-mates/CellsPage'
import { BuilderPage as SmBuilderPage } from '@/features/station-mates/BuilderPage'
import { PledgesPage } from '@/features/station-mates/PledgesPage'
import { BoardPage } from '@/features/station-mates/BoardPage'
import { LiveOpsPage } from '@/features/station-mates/LiveOpsPage'
import { TransportPage } from '@/features/station-mates/TransportPage'
import { IncentivesPage } from '@/features/station-mates/IncentivesPage'
import { IntegrityPage } from '@/features/station-mates/IntegrityPage'
import { AnalyticsPage as SmAnalyticsPage } from '@/features/station-mates/AnalyticsPage'
import { PacePage } from '@/features/station-mates/PacePage'
import { FinanceLayout } from '@/features/finance/FinanceLayout'
import { FinanceAnalysisPage } from '@/features/finance/AnalysisPage'
import { LedgerPage } from '@/features/finance/LedgerPage'
import { PricesPage } from '@/features/finance/PricesPage'
import { StatementPage } from '@/features/finance/StatementPage'
import { ContactsPage } from '@/features/contacts/ContactsPage'
import { CalendarPage } from '@/features/calendar/CalendarPage'
import { EventsPage as FinanceEventsPage } from '@/features/finance/EventsPage'
import { IncidentsLayout } from '@/features/incidents/IncidentsLayout'
import { RegisterPage as IncidentRegisterPage } from '@/features/incidents/RegisterPage'
import { ReportWizard } from '@/features/incidents/ReportWizard'
import { MapPage as IncidentMapPage } from '@/features/incidents/MapPage'
import { AnalyticsPage as IncidentAnalyticsPage } from '@/features/incidents/AnalyticsPage'
import { CandidacyPage } from '@/features/candidacy/CandidacyPage'
import { ImportPage } from '@/features/candidacy/ImportPage'
import { TallyLayout } from '@/features/tally/TallyLayout'
import { LiveBoardPage } from '@/features/tally/LiveBoardPage'
import { SeatDashboardPage } from '@/features/tally/SeatDashboardPage'
import { FormsPage } from '@/features/tally/FormsPage'
import { NominationsLayout } from '@/features/nominations/NominationsLayout'
import { MembersPage } from '@/features/nominations/MembersPage'
import { ContestsPage } from '@/features/nominations/ContestsPage'
import { AspirantsPage } from '@/features/nominations/AspirantsPage'
import { ResultsPage as NominationResultsPage } from '@/features/nominations/ResultsPage'
import { AnalyticsPage as NominationAnalyticsPage } from '@/features/nominations/AnalyticsPage'
import { TallyAnalyticsPage } from '@/features/tally/AnalyticsPage'
import { VoteApp } from '@/features/vote/VoteApp'
import { VerifyPage } from '@/features/vote/VerifyPage'
import { MeetingsPage } from '@/features/meetings/MeetingsPage'
import { MeetingRoomPage } from '@/features/meetings/MeetingRoomPage'
import { GuestJoinPage } from '@/features/meetings/GuestJoinPage'
import { InfrastructurePage } from '@/features/infrastructure/InfrastructurePage'
import { AdvisoryLayout } from '@/features/advisory/AdvisoryLayout'
import { BriefPage } from '@/features/advisory/BriefPage'
import { ConsolePage as AdvisoryConsolePage } from '@/features/advisory/ConsolePage'
import { AnalystPage } from '@/features/advisory/AnalystPage'
import { MapPage as AdvisoryMapPage } from '@/features/advisory/MapPage'
import { FindingsPage } from '@/features/advisory/FindingsPage'
import { ProposalsPage } from '@/features/advisory/ProposalsPage'
import { StudioPage } from '@/features/advisory/StudioPage'
import { RunsPage, RunDetailPage } from '@/features/advisory/RunsPage'
import { ReportPage } from '@/features/advisory/ReportPage'
import { PlaybooksPage } from '@/features/advisory/PlaybooksPage'
import { ModelsPage as AdvisoryModelsPage } from '@/features/advisory/ModelsPage'
import { UsagePage as AdvisoryUsagePage } from '@/features/advisory/UsagePage'
import { DossierLayout } from '@/features/dossier/DossierLayout'
import { SubjectsPage } from '@/features/dossier/SubjectsPage'
import { AssayPage } from '@/features/dossier/AssayPage'
import { DossierAnalyticsPage } from '@/features/dossier/AnalyticsPage'
import { SubjectFilePage } from '@/features/dossier/SubjectFilePage'
import { NewTargetPage } from '@/features/dossier/NewTargetPage'
import { SourcesPage as DossierSourcesPage } from '@/features/dossier/SourcesPage'
import { RunsPage as DossierRunsPage } from '@/features/dossier/RunsPage'
import { ManualEntryPage } from '@/features/dossier/ManualEntryPage'
import { GapsPage } from '@/features/dossier/GapsPage'
import { PlansPage as DossierPlansPage } from '@/features/dossier/PlansPage'
import { AlertsPage as DossierAlertsPage } from '@/features/dossier/AlertsPage'
import { TriagePage } from '@/features/dossier/TriagePage'
import { ImportPage as DossierImportPage } from '@/features/dossier/ImportPage'
import { WatchesPage as DossierWatchesPage } from '@/features/dossier/WatchesPage'
import { CommsLayout } from '@/features/comms/CommsLayout'
import { CommsOverviewPage } from '@/features/comms/OverviewPage'
import { CampaignsPage } from '@/features/comms/CampaignsPage'
import { CampaignPage } from '@/features/comms/CampaignPage'
import { ComposePage } from '@/features/comms/ComposePage'
import { TemplatesPage as CommsTemplatesPage } from '@/features/comms/TemplatesPage'
import { SuppressionsPage } from '@/features/comms/SuppressionsPage'
import { RepliesPage } from '@/features/comms/RepliesPage'
import { ChannelsPage as CommsChannelsPage } from '@/features/comms/ChannelsPage'
import { WarRoomLayout } from '@/features/warroom/WarRoomLayout'
import { IntelligencePage } from '@/features/warroom/IntelligencePage'
import { SituationPage } from '@/features/warroom/SituationPage'
import { WarRoomAnalyticsPage } from '@/features/warroom/AnalyticsPage'
import { ThreadsPage as WarRoomThreadsPage } from '@/features/warroom/ThreadsPage'
import { ThreadPage as WarRoomThreadPage } from '@/features/warroom/ThreadPage'
import { SubjectPage as WarRoomSubjectPage } from '@/features/warroom/SubjectPage'
import { SubjectsPage as WarRoomSubjectsPage } from '@/features/warroom/SubjectsPage'
import { QueuePage as WarRoomQueuePage } from '@/features/warroom/QueuePage'
import { CoordinationPage } from '@/features/warroom/CoordinationPage'
import { LedgerPage as WarRoomLedgerPage } from '@/features/warroom/LedgerPage'
import { AccountsPage as WarRoomAccountsPage } from '@/features/warroom/AccountsPage'
import { WatchesPage as WarRoomWatchesPage } from '@/features/warroom/WatchesPage'
import { OppositionLayout } from '@/features/opposition/OppositionLayout'
import { ThreatMatrixPage } from '@/features/opposition/ThreatMatrixPage'
import { OppositionNewTargetPage } from '@/features/opposition/NewTargetPage'
import { KillChainPage } from '@/features/opposition/KillChainPage'
import { WatchlistPage } from '@/features/opposition/WatchlistPage'
import { ScandalsPage } from '@/features/opposition/ScandalsPage'
import { OperationsPage } from '@/features/opposition/OperationsPage'
import { LiveFeedPage } from '@/features/opposition/LiveFeedPage'
import { SurfacesPage as OppositionSurfacesPage } from '@/features/opposition/SurfacesPage'
import { TargetPage as OppositionTargetPage } from '@/features/opposition/TargetPage'
import { VaultPage } from '@/features/vault/VaultPage'
import { DsarPage } from '@/features/vault/DsarPage'
import { ReturnsPage } from '@/features/tally/ReturnsPage'
import { VariancePage } from '@/features/tally/VariancePage'
import { ReconciliationPage } from '@/features/tally/ReconciliationPage'
import { DisputesPage } from '@/features/tally/DisputesPage'
import { CertificatesPage } from '@/features/tally/CertificatesPage'

function Shell() {
  return (
    <RequireAuth>
      {/*
         The election store sits INSIDE RequireAuth, not at the root.

         It fetches `/elections` on mount, and mounting it above the auth gate would fire
         that call on the login page — an unauthenticated 401 that the api client answers
         by redirecting to /login, which is where we already are. Here it mounts once the
         caller is known, and outside AppShell so the sub-bar's selector and every routed
         page read the same store.
      */}
      <ElectionProvider>
        <AppShell><Outlet /></AppShell>
      </ElectionProvider>
    </RequireAuth>
  )
}

export const router = createBrowserRouter([
  {
    element: <AuthGate><Outlet /></AuthGate>,
    children: [
      { path: '/login', element: <RequireAnonymous><LoginPage /></RequireAnonymous> },
      // Anonymous: the invitee has no session yet — that is the point.
      { path: '/invitation/:token', element: <AcceptInvitationPage /> },
      // Anonymous, and NOT behind RequireAnonymous: somebody with a stale
      // session who has forgotten their password must still be able to reach
      // this. RequireAnonymous would bounce them to the dashboard, which is the
      // one place they cannot get to — that is why they are here.
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/password-change', element: <RequireAuth><ForcedPasswordChangePage /></RequireAuth> },
      // Deliberately OUTSIDE the app shell — see BroadcastStandalonePage's header
      // comment. It guards itself rather than sitting behind RequireAuth, because
      // it must render its own "sign in to broadcast" screen with a `next` back to
      // the exact camera the link named.
      { path: '/broadcast', element: <BroadcastStandalonePage /> },
      /*
         The member voting app. OUTSIDE the shell and OUTSIDE RequireAuth, deliberately:
         a party member is not a staff user, holds a different kind of token on a different
         filter chain, and must never see the admin chrome. It guards itself — the token
         lives in sessionStorage and the app renders its own sign-in.
      */
      { path: '/vote', element: <VoteApp /> },
      /*
         Public certificate verification. No shell, no RequireAuth, no account: the people
         who most need to check a nomination certificate — a returning officer, a journalist
         — are precisely the ones with no login here, and requiring one would mean a document
         can only be verified by the organisation that issued it.
      */
      { path: '/verify', element: <VerifyPage /> },
      /*
         A meeting guest. OUTSIDE the shell and OUTSIDE RequireAuth for the reason the
         voting app gives: somebody holding a join link has no account here, holds a
         different kind of token on a different filter chain, and must never be bounced to
         a staff sign-in screen. It guards itself — there is nothing to guard but the link,
         and the link is the credential.
      */
      { path: '/join/:token', element: <GuestJoinPage /> },
      {
        element: <Shell />,
        children: [
          /*
             The landing page is the platform dashboard, not administration.

             `/` used to redirect to `/administration`, which answers a question almost nobody
             signing in actually has. A mobilizer still lands on their own day at
             `/mobilizers/me`; everybody else arrives here, on the one screen that reads every
             module at once.
          */
          { index: true, element: <DashboardPage /> },
          { path: '/administration', element: <AdministrationModule /> },
          { path: '/users', element: <UsersPage /> },
          { path: '/roles', element: <RolesPage /> },
          { path: '/geography', element: <GeographyPage /> },
          { path: '/elections', element: <ElectionsPage /> },
          { path: '/voters', element: <VotersPage /> },
          /* Platform: what the system is made of, and what it holds about people. */
          {
            // Axis Advisory. The report and run detail routes sit INSIDE the shell so the
            // tab strip stays put when a reader follows a link out of a report — a detail
            // page that loses its module's navigation is a dead end with a back button.
            path: '/advisory',
            element: <AdvisoryLayout />,
            children: [
              { index: true, element: <BriefPage /> },
              { path: 'console', element: <AdvisoryConsolePage /> },
              { path: 'analyst', element: <AnalystPage /> },
              { path: 'map', element: <AdvisoryMapPage /> },
              { path: 'findings', element: <FindingsPage /> },
              { path: 'proposals', element: <ProposalsPage /> },
              { path: 'studio', element: <StudioPage /> },
              { path: 'runs', element: <RunsPage /> },
              { path: 'runs/:id', element: <RunDetailPage /> },
              { path: 'reports/:id', element: <ReportPage /> },
              { path: 'playbooks', element: <PlaybooksPage /> },
              { path: 'models', element: <AdvisoryModelsPage /> },
              { path: 'usage', element: <AdvisoryUsagePage /> },
            ],
          },
          {
            // A dossier is a thing somebody sends to a colleague, so the Subject File is a
            // ROUTE and not the full-screen sheet PollSphere renders — a modal that cannot
            // be linked to gets screenshotted instead, which is the same file leaving the
            // platform with no audit trail.
            path: '/dossier',
            element: <DossierLayout />,
            children: [
              // The Assay is the landing board: the first question about a register is what
              // the record rests on, not which rows exist. The register keeps a real path of
              // its own so it can still be linked to, and the old /dossier/assay redirects
              // rather than rendering a second URL for one screen.
              { index: true, element: <AssayPage /> },
              { path: 'assay', element: <Navigate to="/dossier" replace /> },
              { path: 'analytics', element: <DossierAnalyticsPage /> },
              { path: 'subjects', element: <SubjectsPage /> },
              { path: 'new', element: <NewTargetPage /> },
              { path: 'sources', element: <DossierSourcesPage /> },
              { path: 'runs', element: <DossierRunsPage /> },
              { path: 'entry', element: <ManualEntryPage /> },
              { path: 'gaps', element: <GapsPage /> },
              { path: 'plans', element: <DossierPlansPage /> },
              { path: 'alerts', element: <DossierAlertsPage /> },
              { path: 'triage', element: <TriagePage /> },
              { path: 'import', element: <DossierImportPage /> },
              { path: 'watches', element: <DossierWatchesPage /> },
              // Declared LAST so the literal children above are never shadowed: React
              // Router ranks static over dynamic regardless, but adjacency is what stops
              // somebody adding `/dossier/plans` later without noticing.
              { path: ':id', element: <SubjectFilePage /> },
            ],
          },
          {
            path: '/opposition',
            element: <OppositionLayout />,
            children: [
              { index: true, element: <ThreatMatrixPage /> },
              { path: 'new', element: <OppositionNewTargetPage /> },
              { path: 'live-feed', element: <LiveFeedPage /> },
              { path: 'surfaces', element: <OppositionSurfacesPage /> },
              { path: 'kill-chain', element: <KillChainPage /> },
              { path: 'watchlist', element: <WatchlistPage /> },
              { path: 'scandals', element: <ScandalsPage /> },
              { path: 'operations', element: <OperationsPage /> },
              { path: 'targets/:id', element: <OppositionTargetPage /> },
            ],
          },
          {
            path: '/war-room',
            element: <WarRoomLayout />,
            children: [
              // The console is the landing board: the whole window as one map, at whatever
              // resolution the reader has zoomed to. Situation keeps its ranked-table view at
              // its own path — the two answer different questions and neither replaces the
              // other.
              { index: true, element: <IntelligencePage /> },
              { path: 'situation', element: <SituationPage /> },
              { path: 'analytics', element: <WarRoomAnalyticsPage /> },
              // Declared before `threads/:id` for readability; React Router ranks the
              // static segment above the dynamic one regardless of order.
              { path: 'threads', element: <WarRoomThreadsPage /> },
              { path: 'threads/:id', element: <WarRoomThreadPage /> },
              // One subject, every thread about it, and the council conversation across them.
              { path: 'subjects', element: <WarRoomSubjectsPage /> },
              { path: 'subjects/:id', element: <WarRoomSubjectPage /> },
              { path: 'queue', element: <WarRoomQueuePage /> },
              { path: 'coordination', element: <CoordinationPage /> },
              { path: 'ledger', element: <WarRoomLedgerPage /> },
              { path: 'accounts', element: <WarRoomAccountsPage /> },
              { path: 'watches', element: <WarRoomWatchesPage /> },
            ],
          },
          {
            path: '/comms',
            element: <CommsLayout />,
            children: [
              { index: true, element: <CommsOverviewPage /> },
              // Declared before `campaigns/:id`; React Router ranks the static segment above
              // the dynamic one regardless of order, but keeping them adjacent is what stops
              // somebody adding `/comms/campaigns/new` later without noticing.
              { path: 'campaigns', element: <CampaignsPage /> },
              { path: 'campaigns/:id', element: <CampaignPage /> },
              { path: 'compose', element: <ComposePage /> },
              { path: 'templates', element: <CommsTemplatesPage /> },
              { path: 'suppressions', element: <SuppressionsPage /> },
              { path: 'replies', element: <RepliesPage /> },
              { path: 'channels', element: <CommsChannelsPage /> },
            ],
          },
          { path: '/infrastructure', element: <InfrastructurePage /> },
          { path: '/data-vault', element: <VaultPage /> },
          { path: '/data-vault/requests', element: <DsarPage /> },
          // Declared beside the list, not under it — `/voters/tags` is a literal and
          // there is no `/voters/:id` route for it to be shadowed by. React Router
          // ranks static above dynamic regardless, but keeping them adjacent is what
          // stops somebody adding `:id` later without noticing.
          { path: '/voters/tags', element: <TagManagerPage /> },
          { path: '/integrations', element: <IntegrationsPage /> },
          { path: '/integrations/provider/:code', element: <ProviderPage /> },
          // React Router ranks by specificity rather than declaration order, so
          // `/integrations/provider/:code` already beats `/integrations/:id`.
          // What it does NOT cover is a bare `/integrations/provider`, which has
          // one segment and matches `:id` — sending "provider" to the backend as
          // an integration id, which is a 400 on a UUID column.
          { path: '/integrations/provider', element: <Navigate to="/integrations" replace /> },
          { path: '/integrations/:id', element: <IntegrationDetailPage /> },
          { path: '/transfers', element: <TransfersPage /> },
          // Declared before `/transfers/:id` for readability only — React Router ranks
          // a static segment above a dynamic one regardless of order.
          { path: '/transfers/analysis', element: <TransferAnalysisPage /> },
          { path: '/transfers/:id', element: <TransferDetailPage /> },
          // Every static segment below is declared before it could collide with a
          // dynamic one. React Router ranks static above dynamic regardless of
          // order, but `/mobilizers/me` reaching a `:id` route would send the
          // literal "me" to a uuid column, which is a 400 rather than a 404.
          { path: '/mobilizers', element: <MobilizerRosterPage /> },
          { path: '/mobilizers/me', element: <MyDayPage /> },
          { path: '/mobilizers/hierarchy', element: <MobilizerHierarchyPage /> },
          { path: '/mobilizers/coverage', element: <MobilizerCoveragePage /> },
          { path: '/mobilizers/adoptions', element: <MobilizerAdoptionPage /> },
          { path: '/mobilizers/missions', element: <MobilizerMissionsPage /> },
          { path: '/mobilizers/leaderboard', element: <MobilizerLeaderboardPage /> },
          { path: '/mobilizers/analytics', element: <MobilizerAnalyticsPage /> },
          { path: '/activities', element: <MobilizerActivityPage /> },
          // `/programs` is the list; the twelve analytics screens are children of
          // one layout route so the sub-nav strip is mounted once and the filter
          // state in the query string survives moving between them.
          { path: '/programs', element: <ProgramsListPage /> },
          {
            path: '/programs/analytics',
            element: <ProgramAnalyticsLayout />,
            children: [
              { index: true, element: <ProgramOverviewScreen /> },
              { path: 'council', element: <ProgramCouncilScreen /> },
              { path: 'target-groups', element: <ProgramTargetGroupsScreen /> },
              { path: 'overlap', element: <ProgramOverlapScreen /> },
              { path: 'cost-vs-uplift', element: <ProgramCostVsUpliftScreen /> },
              { path: 'ecosystem', element: <ProgramEcosystemScreen /> },
              { path: 'coverage', element: <ProgramCoverageScreen /> },
              { path: 'geography', element: <ProgramGeographyScreen /> },
              { path: 'money', element: <ProgramMoneyScreen /> },
              { path: 'impact', element: <ProgramImpactScreen /> },
              { path: 'equity', element: <ProgramEquityScreen /> },
              { path: 'quality', element: <ProgramQualityScreen /> },
              { path: 'alerts', element: <ProgramAlertsScreen /> },
            ],
          },
          { path: '/meetings', element: <MeetingsPage /> },
          // The dynamic child AFTER the list, as everywhere else in this file.
          { path: '/meetings/:meetingId', element: <MeetingRoomPage /> },
          { path: '/coverage', element: <LiveCoveragePage /> },
          { path: '/coverage/events', element: <CoverageEventsPage /> },
          { path: '/coverage/archive', element: <CoverageArchivePage /> },
          { path: '/coverage/go-live', element: <CoverageGoLivePage /> },
          { path: '/coverage/assignments', element: <CoverageAssignmentsPage /> },
          { path: '/coverage/map', element: <CoverageMapPage /> },
          { path: '/coverage/analytics', element: <CoverageAnalyticsPage /> },

          /*
           * The Polling Studio: a PARENT route whose layout renders the four
           * groups. Not seventeen sibling routes with a strip copied into each —
           * the strip is one component, the group split lives in it, and
           * `AppShell` is untouched (it takes only `{ children }` and its sub-bar
           * is hardcoded chrome with no slot).
           */
          {
            path: '/polling',
            element: <PollingLayout />,
            children: [
              // The Estimator is the landing board: the first question about a poll is what
              // its number can bear, not what is in the field this hour. Overview keeps a real
              // path of its own so it can still be linked to.
              { index: true, element: <EstimatorPage /> },
              { path: 'overview', element: <PollingOverviewPage /> },
              { path: 'polls', element: <PollsListPage /> },
              { path: 'fieldwork', element: <FieldworkPage /> },
              { path: 'my-work', element: <MyAssignmentsPage /> },
              { path: 'quality', element: <QualityPage /> },
              { path: 'interviews', element: <InterviewsPage /> },
              { path: 'map', element: <MapPage /> },
              { path: 'configure', element: <ConfiguratorPage /> },
              { path: 'builder', element: <BuilderPage /> },
              { path: 'sample', element: <SamplePage /> },
              { path: 'trackers', element: <TrackersPage /> },
              { path: 'results', element: <ResultsPage /> },
              { path: 'crosstabs', element: <CrosstabsPage /> },
              { path: 'segments', element: <SegmentsPage /> },
              { path: 'verbatims', element: <VerbatimsPage /> },
              { path: 'exports', element: <ExportsPage /> },
              { path: 'external', element: <ExternalPollsPage /> },
            ],
          },

          /**
           * Capture is a CHILD of nothing.
           *
           * It is the one polling screen used standing up, outdoors, one question
           * at a time — the group strip above it would be four rows of navigation
           * an interviewer must scroll past on every question, and a stray tap on
           * one of them abandons an interview in progress. So it takes the full
           * content area and links back to My work when it is finished.
           */
          { path: '/polling/capture', element: <CapturePage /> },

          /**
           * The political landscape. Same parent-route arrangement as polling, and
           * four tabs rather than four groups of tabs — four is not seventeen.
           */
          {
            path: '/political-landscape',
            element: <PoliticsLayout />,
            children: [
              { index: true, element: <PoliticsOverviewPage /> },
              { path: 'parties', element: <PartiesPage /> },
              { path: 'coalitions', element: <CoalitionsPage /> },
              { path: 'candidates', element: <CandidatesPage /> },
            ],
          },

          /** Know Your Polling Station Mate (Phase 6) */
          {
            path: '/station-mates',
            element: <StationMatesLayout />,
            children: [
              // The Floor is the landing board: on polling day the first question is who is
              // still out and whether there is time, not what the register looks like.
              // Overview keeps a real path so it can still be linked to.
              { index: true, element: <FloorPage /> },
              { path: 'overview', element: <StationMatesOverviewPage /> },
              { path: 'stations', element: <StationsPage /> },
              { path: 'cells', element: <CellsPage /> },
              { path: 'builder', element: <SmBuilderPage /> },
              { path: 'pledges', element: <PledgesPage /> },
              { path: 'board', element: <BoardPage /> },
              { path: 'live', element: <LiveOpsPage /> },
              // A real route rather than a query parameter: the pace report is opened from
              // an email, on a phone, by somebody who has not used the console before.
              { path: 'pace', element: <PacePage /> },
              { path: 'transport', element: <TransportPage /> },
              { path: 'incentives', element: <IncentivesPage /> },
              { path: 'integrity', element: <IntegrityPage /> },
              { path: 'analytics', element: <SmAnalyticsPage /> },
            ],
          },

          { path: 'contacts', element: <ContactsPage /> },
          { path: 'calendar', element: <CalendarPage /> },

          {
            // Campaign finance. The analysis is the landing tab, not the ledger: the first
            // question somebody opening this has is "how much have we spent and on what",
            // and the ledger is where they go once they want to see the lines.
            path: 'finance',
            element: <FinanceLayout />,
            children: [
              { index: true, element: <FinanceAnalysisPage /> },
              { path: 'ledger', element: <LedgerPage /> },
              { path: 'prices', element: <PricesPage /> },
              { path: 'statement', element: <StatementPage /> },
              { path: 'events', element: <FinanceEventsPage /> },
            ],
          },

          /**
           * Vote Tallying Centre (Phase 9).
           *
           * The ballot is the module's FIRST tab, not a separate destination: nothing can be
           * tallied until a ballot exists, and splitting the two across menus would make the
           * prerequisite invisible. The backend routes still live under `politics` — a
           * candidacy is a politics concept that tallying consumes.
           */
          {
            path: '/nominations',
            element: <NominationsLayout />,
            children: [
              { index: true, element: <MembersPage /> },
              { path: 'contests', element: <ContestsPage /> },
              { path: 'aspirants', element: <AspirantsPage /> },
              { path: 'results', element: <NominationResultsPage /> },
              { path: 'analytics', element: <NominationAnalyticsPage /> },
            ],
          },
          {
            
            path: '/tallying',
            element: <TallyLayout />,
            children: [
              { index: true, element: <CandidacyPage /> },
              /*
                 Import has its own destination, not just a modal on the register.

                 An import is a RECORD, not a one-off action: "where did these four thousand
                 candidacies come from" is asked months later by somebody who was not in the
                 room, and the answer is a batch. Reachable from the tab because the tab
                 promised it — it shipped dimmed and unrouted for a release, which meant a
                 finished wizard nobody could open.
              */
              { path: 'import', element: <ImportPage /> },
              /* Count */
              { path: 'board', element: <LiveBoardPage /> },
              { path: 'seats', element: <SeatDashboardPage /> },
              { path: 'forms', element: <FormsPage /> },
              { path: 'returns', element: <ReturnsPage /> },
              { path: 'analytics', element: <TallyAnalyticsPage /> },
              /* Assure */
              { path: 'variance', element: <VariancePage /> },
              { path: 'reconciliation', element: <ReconciliationPage /> },
              { path: 'disputes', element: <DisputesPage /> },
              { path: 'certificates', element: <CertificatesPage /> },
            ],
          },

          /** Incident Desk (Phase 8) */
          {
            path: '/incidents',
            element: <IncidentsLayout />,
            children: [
              { index: true, element: <IncidentRegisterPage /> },
              { path: 'report', element: <ReportWizard /> },
              { path: 'map', element: <IncidentMapPage /> },
              { path: 'analytics', element: <IncidentAnalyticsPage /> },
            ],
          },

          { path: '/victory', element: <VictoryPage /> },
          { path: '/history', element: <HistoryPage /> },
          { path: '/audit', element: <AuditLogPage /> },
          { path: '/notifications', element: <NotificationsPage /> },
          { path: '*', element: <Navigate to="/administration" replace /> },
        ],
      },
    ],
  },
])
